import React, { useCallback, useMemo, useState } from "react";
import {
    Box,
    Tab,
    Tabs,
    Typography,
    IconButton,
    Paper,
    Divider,
    TextField,
    FormHelperText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import {
    Controller,
    useWatch,
    useFormContext,
    FormProvider,
    useForm,
} from "react-hook-form";

const tabStyles = {
    minWidth: 100,
    fontWeight: 500,
};

const dropzoneStyle = {
    border: "2px dashed #ccc",
    padding: "1.5rem",
    textAlign: "center",
    borderRadius: 8,
    backgroundColor: "#f9f9f9",
    cursor: "pointer",
};

interface ImageUploadInputProps {
    // React Hook Form props
    control?: any;
    name?: string;

    // Plain onChange props
    value?: File | null;
    onChange?: (file: File | null) => void;
    logoUrl?: string;
    onLogoUrlChange?: (url: string) => void;

    // Common props
    label?: string;
    onDragLabel?: string;
    acceptedFileTypes?: string[];
    error?: boolean;
    helperText?: string;
}

/**
 * ImageUploadInput - Supports both React Hook Form and plain onChange
 *
 * Usage with React Hook Form (existing FormProvider from parent):
 * <ImageUploadInput control={control} name="logo" />
 *
 * Usage with plain onChange (automatically wrapped in FormProvider):
 * <ImageUploadInput value={file} onChange={handleChange} logoUrl={url} onLogoUrlChange={handleUrlChange} />
 */

const ImageUploadInput = ({
    control,
    name,
    value: externalValue,
    onChange,
    logoUrl: externalLogoUrl,
    onLogoUrlChange,
    label = "Drop logo image here or click to upload",
    onDragLabel = "Drop logo image here",
    acceptedFileTypes = ["image/png", "image/jpeg"],
    error = false,
    helperText,
}: ImageUploadInputProps) => {
    // Always call hooks unconditionally to follow rules of hooks
    const formContext = useFormContext();
    const setValue = formContext?.setValue;
    const [activeTab, setActiveTab] = useState(0);

    // Determine if we're in RHF mode
    // RHF mode: control + name provided, and setValue exists (FormProvider present)
    const isRHFMode = control && name && setValue;

    // React Hook Form mode - useWatch always called but only active when in RHF mode
    const rhfValue = useWatch({ control, name, disabled: !isRHFMode }) || null;
    const logoUrlFromForm =
        useWatch({ control, name: "logo_url", disabled: !isRHFMode }) || "";

    // Use external value if provided (plain mode), otherwise use RHF value
    const value = externalValue !== undefined ? externalValue : rhfValue;
    const logoUrl =
        externalLogoUrl !== undefined ? externalLogoUrl : logoUrlFromForm;

    const onDrop = useCallback(
        async (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (acceptedFileTypes.includes(file?.type)) {
                if (isRHFMode) {
                    setValue("logo_url", ""); // clear URL if upload happens
                    setValue(name, file);
                } else {
                    onLogoUrlChange?.(""); // clear URL if upload happens
                    onChange?.(file);
                }
            } else {
                alert("Unsupported file type");
                if (isRHFMode) {
                    setValue(name, null);
                } else {
                    onChange?.(null);
                }
            }
        },
        [
            acceptedFileTypes,
            isRHFMode,
            name,
            setValue,
            onChange,
            onLogoUrlChange,
        ],
    );

    const { getRootProps, getInputProps, isDragActive, isDragReject } =
        useDropzone({
            onDrop,
            multiple: false,
        });

    const handleDelete = () => {
        if (isRHFMode) {
            setValue(name, null);
            setValue("logo_url", "");
        } else {
            onChange?.(null);
            onLogoUrlChange?.("");
        }
    };

    const localURL = useMemo(() => {
        if (value) {
            try {
                return URL.createObjectURL(value);
            } catch {
                return null;
            }
        }
        return null;
    }, [value]);

    return (
        <Box>
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                borderWidth: "2px",
                borderColor: error ? "error.main" : "#e0e0e0",
            }}
        >
            <Box display="flex" flexWrap="wrap">
                {/* Preview */}
                <Box
                    sx={{
                        background: "white",
                        padding: "12px",
                        backgroundColor: "white",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    {localURL || logoUrl ? (
                        <a
                            href={localURL || logoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={localURL || logoUrl}
                                alt="preview"
                                style={{
                                    width: 128,
                                    height: 128,
                                    objectFit: "contain",
                                    cursor: "pointer",
                                }}
                            />
                        </a>
                    ) : (
                        <Box
                            width={128}
                            height={128}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            border="1px dashed #ccc"
                            borderRadius={1}
                            color="text.secondary"
                            fontSize={12}
                        >
                            No Image
                        </Box>
                    )}
                </Box>

                {/* Right side (tabs and fields) */}
                <Box flex={1} p={2} maxWidth="100%">
                    <Tabs
                        value={activeTab}
                        onChange={(_, newTab) => setActiveTab(newTab)}
                        indicatorColor="primary"
                        textColor="primary"
                        sx={{
                            mb: 2,
                            "& .MuiTabs-indicator": {
                                backgroundColor: "warning.main",
                            },
                        }}
                    >
                        <Tab
                            label="Upload"
                            sx={{
                                ...tabStyles,
                                "&.Mui-selected": {
                                    color: "warning.main",
                                },
                            }}
                        />
                        <Tab
                            label="URL"
                            sx={{
                                ...tabStyles,
                                "&.Mui-selected": {
                                    color: "warning.main",
                                },
                            }}
                        />
                    </Tabs>

                    <Divider sx={{ mb: 2 }} />

                    {activeTab === 0 && (
                        <Box
                            {...getRootProps()}
                            sx={{
                                ...dropzoneStyle,
                                borderColor: isDragReject
                                    ? "error.main"
                                    : isDragActive
                                    ? "warning.main"
                                    : "#ccc",
                            }}
                        >
                            <input {...getInputProps()} />
                            <Typography variant="body2" color="textSecondary">
                                {value ? onDragLabel : label}
                            </Typography>
                        </Box>
                    )}

                    {activeTab === 1 &&
                        (isRHFMode ? (
                            <Controller
                                name="logo_url"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        label="Logo URL"
                                        value={field.value || ""}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setValue(name, null); // clear uploaded file if URL is set
                                        }}
                                        fullWidth
                                    />
                                )}
                            />
                        ) : (
                            <TextField
                                label="Logo URL"
                                value={logoUrl || ""}
                                onChange={(e) => {
                                    onChange?.(null); // clear uploaded file if URL is set
                                    onLogoUrlChange?.(e.target.value);
                                }}
                                fullWidth
                            />
                        ))}

                    {(value || logoUrl) && (
                        <Box display="flex" alignItems="center" mt={2}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {value?.name}
                            </Typography>
                            <IconButton onClick={handleDelete} size="small">
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
        {helperText && (
            <FormHelperText error={error} sx={{ mt: 0.5, ml: 1.5 }}>
                {helperText}
            </FormHelperText>
        )}
        </Box>
    );
};

// Wrapper component that handles plain mode without requiring external FormProvider
const ImageUploadInputWrapper = (props: ImageUploadInputProps) => {
    // Always call hooks unconditionally
    const methods = useForm();

    // Determine if we need to wrap in FormProvider
    const needsWrapper =
        !(props.control && props.name) &&
        (props.value !== undefined || props.onChange !== undefined);

    if (needsWrapper) {
        return (
            <FormProvider {...methods}>
                <ImageUploadInput {...props} />
            </FormProvider>
        );
    }

    // RHF mode - FormProvider expected from parent
    return <ImageUploadInput {...props} />;
};

export default ImageUploadInputWrapper;
